// QR Kiosk v2 — KV2-3 form atoms (inline styles + scoped tokens; NO data logic).
// FLabel / FieldError / inputStyle() / KTextInput / KTextArea / KMoneyInput.
// All field boxes derive from the ONE inputStyle() helper (§3.1). RHF-friendly:
// the text inputs forwardRef so `{...register(...)}` (which carries a ref) wires through.
// Tokens only — no MUI / dt / hex. Kiosk-legibility pass 2 (review §4): labels
// 13.5, fields 52px/15px. KDisclosure was REMOVED with its only consumer (the
// "Happened earlier?" openDate disclosure — owner override of D6).
import React from "react";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";

export interface FLabelProps {
  children: React.ReactNode;
  req?: boolean;
}
export function FLabel({ children, req }: FLabelProps) {
  return (
    <label style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--ink-2)", marginBottom: 6 }}>
      {children}
      {req && <span style={{ color: "color-mix(in oklab, var(--red) 80%, var(--ink))" }}> *</span>}
    </label>
  );
}

export function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginTop: 5,
        fontSize: 12.5,
        fontWeight: 600,
        color: "color-mix(in oklab, var(--red) 80%, var(--ink))",
      }}
    >
      <span style={{ fontSize: 12 }}>{"⚠"}</span>
      {children}
    </div>
  );
}

/** The ONE source of field-box styling (§3.1). error variant tints the border + fill red. */
export function inputStyle(error?: boolean): React.CSSProperties {
  return {
    width: "100%",
    height: 52,
    borderRadius: 11,
    border: error ? "1px solid color-mix(in oklab, var(--red) 55%, var(--border))" : "1px solid var(--border)",
    background: error ? "color-mix(in oklab, var(--red) 4%, var(--card))" : "var(--card)",
    padding: "0 13px",
    fontSize: 15,
    fontFamily: "inherit",
    color: "var(--ink)",
    outline: "none",
    boxSizing: "border-box",
    colorScheme: "light",
  };
}

export interface KFieldTriggerProps {
  value?: string;
  placeholder: string;
  error?: boolean;
  lines?: number; // 1 = title; 3 = description/notes preview clamp
  onOpen: () => void;
}

/** §7.2 read-only trigger row — looks like a field, acts like a button. Derived from the
 *  ONE inputStyle() helper so error/normal tinting stays unified. Opens FieldEditor.
 *  Empty → placeholder in --faint (normal style — NEVER italic). */
export function KFieldTrigger({ value, placeholder, error, lines = 1, onOpen }: KFieldTriggerProps) {
  const press = usePressed();
  const multiline = lines > 1;
  const hasValue = !!value;
  const previewClamp: React.CSSProperties = multiline
    ? { display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: lines, overflow: "hidden" }
    : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
  return (
    <button
      type="button"
      {...press.pressHandlers}
      onClick={onOpen}
      style={{
        ...inputStyle(error),
        height: "auto",
        minHeight: multiline ? 92 : 52,
        padding: "13px",
        display: "flex",
        gap: 10,
        alignItems: multiline ? "flex-start" : "center",
        textAlign: "left",
        cursor: "pointer",
        ...press.pressStyle,
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 15,
          fontWeight: 500,
          lineHeight: 1.5,
          color: hasValue ? "var(--ink)" : "var(--faint)",
          ...previewClamp,
        }}
      >
        {hasValue ? value : placeholder}
      </span>
      <span
        style={{ color: "var(--muted)", flexShrink: 0, display: "inline-flex", alignSelf: multiline ? "flex-start" : "center" }}
      >
        <Glyph d={GD.pen} size={16} />
      </span>
    </button>
  );
}

export interface KTextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}
export const KTextInput = React.forwardRef<HTMLInputElement, KTextInputProps>(
  ({ error, style, ...rest }, ref) => (
    <input ref={ref} type="text" style={{ ...inputStyle(error), ...style }} {...rest} />
  )
);
KTextInput.displayName = "KTextInput";

export interface KTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  h?: number;
}
export const KTextArea = React.forwardRef<HTMLTextAreaElement, KTextAreaProps>(
  ({ error, h = 92, style, ...rest }, ref) => {
    const base = inputStyle(error);
    return (
      <textarea
        ref={ref}
        style={{ ...base, height: h, padding: "11px 13px", resize: "none", lineHeight: 1.5, ...style }}
        {...rest}
      />
    );
  }
);
KTextArea.displayName = "KTextArea";

export interface KMoneyInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}
// Explicit `EUR €` suffix — the currency label of the bug-#1 fix (id set in submit, one writer).
export const KMoneyInput = React.forwardRef<HTMLInputElement, KMoneyInputProps>(
  ({ error, style, ...rest }, ref) => {
    const box = inputStyle(error);
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: 52,
          borderRadius: 11,
          border: box.border,
          background: box.background,
          padding: "0 13px",
        }}
      >
        <input
          ref={ref}
          type="number"
          inputMode="decimal"
          step="0.01"
          min={0}
          style={{
            flex: 1,
            border: "none",
            background: "transparent",
            outline: "none",
            fontFamily: "var(--font-mono)",
            fontSize: 17,
            fontWeight: 600,
            color: "var(--ink)",
            width: "100%",
            MozAppearance: "textfield",
            ...style,
          }}
          {...rest}
        />
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--muted)", flexShrink: 0 }}>
          EUR €
        </span>
      </div>
    );
  }
);
KMoneyInput.displayName = "KMoneyInput";
